import type { CacheLocation } from '@auth0/auth0-spa-js'

// `clientId` is intentionally absent — it's an `AuthProvider` prop, swapped
// per-render for the student-auth client variant.
export type Auth0Config = {
  provider: 'auth0'
  domain: string
  audience: string
  scope: string
  cacheLocation?: CacheLocation
  legacySameSiteCookie?: boolean
}

export type OidcConfig = {
  provider: 'oidc'
  authority: string
  audience: string
  scope: string
  postLogoutRedirectUri: string
}

export type AuthConfig = Auth0Config | OidcConfig

// Wire shape of config.yaml's `auth` block (nested sub-objects), not the resolved `AuthConfig`.
export interface RawAuthBlock {
  provider: 'auth0' | 'oidc'
  auth0?: {
    domain: string
    audience: string
    clientId: string
    organizationId: string
    studentClientId: string
    cacheLocation?: CacheLocation
  }
  oidc?: {
    authority: string
    clientId?: string
    audience?: string
    scope?: string
    postLogoutRedirectUri?: string
  }
}

interface AuthConfigDefaults {
  scope: string
  oidcPostLogoutRedirectUri: string
}

interface ProviderCredentials {
  clientId: string
  domain: string
  audience: string
  organizationId: string
  studentClientId: string
}

const EMPTY_CREDENTIALS: ProviderCredentials = {
  clientId: '',
  domain: '',
  audience: '',
  organizationId: '',
  studentClientId: ''
}

export function resolveProviderCredentials(auth?: RawAuthBlock): ProviderCredentials {
  if (auth?.provider === 'oidc') {
    return { ...EMPTY_CREDENTIALS, clientId: auth.oidc?.clientId ?? '' }
  }
  if (!auth?.auth0) {
    return EMPTY_CREDENTIALS
  }
  const { clientId, domain, audience, organizationId, studentClientId } = auth.auth0
  return { clientId, domain, audience, organizationId, studentClientId }
}

// Static runtime config; the per-render AuthProvider props come from resolveProviderCredentials.
export function getAuthConfig(config: { auth?: RawAuthBlock }, defaults: AuthConfigDefaults): AuthConfig {
  const auth = config.auth
  // config.yaml is untyped wire data; an absent/legacy block predates `provider` → auth0.
  const provider: string = auth?.provider ?? 'auth0'
  switch (provider) {
    case 'auth0':
      return buildAuth0Config(auth, defaults)
    case 'oidc':
      return buildOidcConfig(auth as RawAuthBlock, defaults)
    default:
      throw new Error(`Invalid config.yaml: unknown "auth.provider" "${provider}" (expected "auth0" or "oidc").`)
  }
}

function buildOidcConfig(auth: RawAuthBlock, defaults: AuthConfigDefaults): OidcConfig {
  if (!auth.oidc) {
    throw new Error(`Invalid config.yaml: "auth.provider" is "oidc" but the "auth.oidc" block is missing.`)
  }
  if (!auth.oidc.audience) {
    throw new Error(`Invalid config.yaml: "auth.oidc.audience" is required (CARTO APIs validate the token audience).`)
  }
  return {
    provider: 'oidc',
    authority: auth.oidc.authority,
    audience: auth.oidc.audience,
    scope: auth.oidc.scope ?? defaults.scope,
    postLogoutRedirectUri: auth.oidc.postLogoutRedirectUri ?? defaults.oidcPostLogoutRedirectUri
  }
}

function buildAuth0Config(auth: RawAuthBlock | undefined, defaults: AuthConfigDefaults): Auth0Config {
  if (!auth?.auth0) {
    throw new Error(`Invalid config.yaml: expected an "auth.auth0" block, or "auth.provider: oidc".`)
  }
  return {
    provider: 'auth0',
    domain: auth.auth0.domain,
    audience: auth.auth0.audience,
    scope: defaults.scope,
    // No default here: each app picks its own fallback at the provider (accounts-www: memory,
    // workspace-www: localstorage) — a package-wide default silently overrides that choice.
    cacheLocation: auth.auth0.cacheLocation,
    legacySameSiteCookie: false
  }
}
